import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Link,
  Divider,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  LocalHospital as HospitalIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';

interface RegisterForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  date_of_birth: string;
  national_id: string;
  gender?: 'male' | 'female' | 'other';
  address?: string;
  city?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  accepts_terms: boolean;
  accepts_privacy: boolean;
  accepts_notifications: boolean;
}

const steps = ['Información Personal', 'Datos de Contacto', 'Contacto de Emergencia', 'Confirmación'];

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    trigger,
    getValues,
    control,
  } = useForm<RegisterForm>();

  const password = watch('password');

  const formatChileanPhone = (input: string): string => {
    // Remove all non-digits
    let digits = input.replace(/\D/g, '');
    
    // Remove leading country code if present
    if (digits.startsWith('56')) {
      digits = digits.slice(2);
    }
    
    // Format based on length
    if (digits.length === 0) return '';
    if (digits.length <= 1) return '+56 ' + digits;
    if (digits.length <= 5) return '+56 ' + digits[0] + ' ' + digits.slice(1);
    if (digits.length <= 9) {
      return '+56 ' + digits[0] + ' ' + digits.slice(1, 5) + ' ' + digits.slice(5);
    }
    
    // Limit to 9 digits total (format: +56 9 1234 5678)
    digits = digits.slice(0, 9);
    return '+56 ' + digits[0] + ' ' + digits.slice(1, 5) + ' ' + digits.slice(5);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>, onChange: (value: string) => void) => {
    const input = e.target.value;
    const formatted = formatChileanPhone(input);
    onChange(formatted);
    e.target.value = formatted;
  };

  const formatChileanRUT = (input: string): string => {
    // Remove all non-alphanumeric characters
    const cleaned = input.replace(/[^\dkK]/g, '').toUpperCase();
    
    if (cleaned.length === 0) return '';
    if (cleaned.length === 1) return cleaned;
    
    // Handle the case where we have at least 2 characters
    const body = cleaned.slice(0, -1);
    const dv = cleaned.slice(-1);
    
    // Format the body with dots every 3 digits from right to left
    let formattedBody = '';
    for (let i = body.length - 1, j = 0; i >= 0; i--, j++) {
      if (j > 0 && j % 3 === 0) {
        formattedBody = '.' + formattedBody;
      }
      formattedBody = body[i] + formattedBody;
    }
    
    return `${formattedBody}-${dv}`;
  };

  const handleRUTChange = (e: React.ChangeEvent<HTMLInputElement>, onChange: (value: string) => void) => {
    const input = e.target.value;
    const formatted = formatChileanRUT(input);
    
    // Limit to maximum RUT length (12.345.678-9 = 12 characters)
    if (formatted.length <= 12) {
      onChange(formatted);
      // Update the input value directly to show formatting
      e.target.value = formatted;
    }
  };

  const validateStep = async (step: number): Promise<boolean> => {
    const fieldsToValidate: (keyof RegisterForm)[] = [];
    
    switch (step) {
      case 0:
        fieldsToValidate.push('first_name', 'last_name', 'date_of_birth', 'national_id', 'gender');
        break;
      case 1:
        fieldsToValidate.push('email', 'phone', 'password', 'confirmPassword');
        break;
      case 2:
        fieldsToValidate.push('emergency_contact_name', 'emergency_contact_phone', 'emergency_contact_relationship');
        break;
      case 3:
        fieldsToValidate.push('accepts_terms', 'accepts_privacy');
        break;
    }

    const result = await trigger(fieldsToValidate);
    return result;
  };

  const handleNext = async () => {
    const isStepValid = await validateStep(activeStep);
    if (isStepValid) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onSubmit = async (data: RegisterForm) => {
    setIsLoading(true);
    setError('');

    try {
      // Clean and format data for API
      const registrationData = {
        ...data,
        // Keep phone numbers as they are (already formatted)
        phone: data.phone,
        emergency_contact_phone: data.emergency_contact_phone,
        // Clean RUT for API (remove dots and dash for validation)
        national_id: data.national_id,
      };

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/v1/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Error en el registro');
      }

      // Store tokens
      localStorage.setItem('token', result.data.token);
      localStorage.setItem('refresh_token', result.data.refresh_token);
      localStorage.setItem('user', JSON.stringify(result.data.user));

      // Redirect to verification page
      navigate('/verify', { 
        state: { 
          email: data.email, 
          phone: data.phone,
          user: result.data.user 
        } 
      });

    } catch (error: any) {
      setError(error.message || 'Error en el registro');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Nombre"
                error={!!errors.first_name}
                helperText={errors.first_name?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                {...register('first_name', { required: 'El nombre es requerido' })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Apellido"
                error={!!errors.last_name}
                helperText={errors.last_name?.message}
                {...register('last_name', { required: 'El apellido es requerido' })}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="national_id"
                control={control}
                rules={{ 
                  required: 'El RUT es requerido',
                  pattern: {
                    value: /^\d{1,2}\.\d{3}\.\d{3}-[\dkK]$/,
                    message: 'Formato de RUT inválido'
                  }
                }}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    fullWidth
                    label="RUT"
                    placeholder="12.345.678-9"
                    value={value || ''}
                    error={!!errors.national_id}
                    helperText={errors.national_id?.message || 'Ejemplo: 12.345.678-9'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e) => handleRUTChange(e, onChange)}
                    inputProps={{
                      maxLength: 12,
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Fecha de Nacimiento"
                type="date"
                InputLabelProps={{ shrink: true }}
                error={!!errors.date_of_birth}
                helperText={errors.date_of_birth?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                {...register('date_of_birth', { required: 'La fecha de nacimiento es requerida' })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Género</InputLabel>
                <Select
                  label="Género"
                  error={!!errors.gender}
                  {...register('gender', { required: 'Selecciona tu género' })}
                >
                  <MenuItem value="male">Masculino</MenuItem>
                  <MenuItem value="female">Femenino</MenuItem>
                  <MenuItem value="other">Otro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Correo Electrónico"
                type="email"
                error={!!errors.email}
                helperText={errors.email?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                {...register('email', {
                  required: 'El correo electrónico es requerido',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Correo electrónico inválido',
                  },
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: 'El teléfono es requerido',
                  pattern: {
                    value: /^\+56\s[9|2-9]\s\d{4}\s\d{4}$/,
                    message: 'Número de teléfono chileno inválido'
                  }
                }}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    fullWidth
                    label="Teléfono"
                    placeholder="+56 9 1234 5678"
                    value={value || ''}
                    error={!!errors.phone}
                    helperText={errors.phone?.message || 'Formato: +56 9 1234 5678'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    onChange={(e) => handlePhoneChange(e, onChange)}
                    inputProps={{
                      maxLength: 17, // +56 9 1234 5678 = 17 characters
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...register('password', {
                  required: 'La contraseña es requerida',
                  minLength: {
                    value: 8,
                    message: 'La contraseña debe tener al menos 8 caracteres',
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                    message: 'Debe contener mayúscula, minúscula y número'
                  }
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Confirmar Contraseña"
                type={showConfirmPassword ? 'text' : 'password'}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...register('confirmPassword', {
                  required: 'Confirma tu contraseña',
                  validate: (value) => value === password || 'Las contraseñas no coinciden'
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Dirección (Opcional)"
                {...register('address')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Ciudad (Opcional)"
                {...register('city')}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Contacto de Emergencia
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Esta información es crucial para situaciones de emergencia médica.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del Contacto"
                error={!!errors.emergency_contact_name}
                helperText={errors.emergency_contact_name?.message}
                {...register('emergency_contact_name', { required: 'El nombre del contacto es requerido' })}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="emergency_contact_phone"
                control={control}
                rules={{
                  required: 'El teléfono del contacto es requerido',
                  pattern: {
                    value: /^\+56\s[9|2-9]\s\d{4}\s\d{4}$/,
                    message: 'Número de teléfono chileno inválido'
                  }
                }}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    fullWidth
                    label="Teléfono del Contacto"
                    placeholder="+56 9 1234 5678"
                    value={value || ''}
                    error={!!errors.emergency_contact_phone}
                    helperText={errors.emergency_contact_phone?.message || 'Formato: +56 9 1234 5678'}
                    onChange={(e) => handlePhoneChange(e, onChange)}
                    inputProps={{
                      maxLength: 17,
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Relación</InputLabel>
                <Select
                  label="Relación"
                  error={!!errors.emergency_contact_relationship}
                  {...register('emergency_contact_relationship', { required: 'Selecciona la relación' })}
                >
                  <MenuItem value="spouse">Cónyuge</MenuItem>
                  <MenuItem value="parent">Padre/Madre</MenuItem>
                  <MenuItem value="child">Hijo/Hija</MenuItem>
                  <MenuItem value="sibling">Hermano/Hermana</MenuItem>
                  <MenuItem value="friend">Amigo/Amiga</MenuItem>
                  <MenuItem value="other">Otro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Términos y Condiciones
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    {...register('accepts_terms', { required: 'Debes aceptar los términos y condiciones' })}
                  />
                }
                label={
                  <Typography variant="body2">
                    Acepto los{' '}
                    <Link component="button" variant="body2">
                      Términos y Condiciones
                    </Link>{' '}
                    del servicio IAM-ACV
                  </Typography>
                }
              />
              {errors.accepts_terms && (
                <Typography variant="caption" color="error">
                  {errors.accepts_terms.message}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    {...register('accepts_privacy', { required: 'Debes aceptar la política de privacidad' })}
                  />
                }
                label={
                  <Typography variant="body2">
                    Acepto la{' '}
                    <Link component="button" variant="body2">
                      Política de Privacidad
                    </Link>{' '}
                    y el tratamiento de mis datos personales
                  </Typography>
                }
              />
              {errors.accepts_privacy && (
                <Typography variant="caption" color="error">
                  {errors.accepts_privacy.message}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox {...register('accepts_notifications')} />}
                label={
                  <Typography variant="body2">
                    Deseo recibir notificaciones sobre actualizaciones del servicio y consejos de salud (opcional)
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Resumen de tu registro:</strong><br />
                  Nombre: {getValues('first_name')} {getValues('last_name')}<br />
                  Email: {getValues('email')}<br />
                  Teléfono: {getValues('phone')}<br />
                  Contacto de emergencia: {getValues('emergency_contact_name')} ({getValues('emergency_contact_phone')})
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%', m: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
            <HospitalIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold">
                Registro IAM-ACV
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Crea tu cuenta para acceder al sistema de emergencias
              </Typography>
            </Box>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {renderStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
              >
                Atrás
              </Button>
              
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isLoading}
                  sx={{ minWidth: 120 }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Registrarse'
                  )}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Siguiente
                </Button>
              )}
            </Box>
          </form>

          <Divider sx={{ my: 3 }} />

          <Box textAlign="center">
            <Typography variant="body2">
              ¿Ya tienes una cuenta?{' '}
              <Link component={RouterLink} to="/login" variant="body2">
                Inicia Sesión
              </Link>
            </Typography>
          </Box>

          <Box mt={4} textAlign="center">
            <Typography variant="caption" color="text.secondary">
              © 2024 IAM-ACV. Todos los derechos reservados.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Register;